import { Dialog as DialogPrimitive } from '@base-ui/react/dialog';
import { useMergedRefs } from '@base-ui/utils/useMergedRefs';
import { XIcon } from 'lucide-react';
import { useRef, type ComponentPropsWithRef, type CSSProperties, type ReactElement } from 'react';
import { IconButton } from '../candidates/icon-button.tsx';
import { Button, type ButtonVariantProps } from '../primitives/button.tsx';
import { cn } from '../util/classes.ts';
import { Container } from './container.tsx';

type RootPrimitiveProps = Omit<DialogPrimitive.Root.Props, 'onOpenChange'> & {
    onOpenChange?: (open: boolean) => void;
};

type BackdropPrimitiveProps = Omit<DialogPrimitive.Backdrop.Props, 'className' | 'render' | 'style'> & {
    className?: string;
    style?: CSSProperties;
};

type PopupPrimitiveProps = Omit<DialogPrimitive.Popup.Props, 'className' | 'render' | 'style'> & {
    className?: string;
    style?: CSSProperties;
};

/** Which edge the panel is docked to. Every part carries it as `data-side`. */
export type DrawerSide = 'right' | 'left';

export type DrawerProps = RootPrimitiveProps & {
    /** Called on every close path. Fires alongside `onOpenChange`, not instead of it. */
    onClose?: () => void;
};

export function Drawer({ onOpenChange, onClose, ...props }: DrawerProps): ReactElement {
    return (
        <DialogPrimitive.Root
            {...props}
            onOpenChange={(open) => {
                onOpenChange?.(open);
                if (!open) {
                    onClose?.();
                }
            }}
        />
    );
}

export type DrawerTriggerProps = ComponentPropsWithRef<'button'> &
    ButtonVariantProps & {
        render?: ReactElement;
    };

export function DrawerTrigger({ render, children, ...props }: DrawerTriggerProps): ReactElement {
    return (
        <DialogPrimitive.Trigger data-slot="drawer-trigger" render={render ?? <Button {...props} />}>
            {children}
        </DialogPrimitive.Trigger>
    );
}

export type DrawerPortalProps = DialogPrimitive.Portal.Props;

export function DrawerPortal({ children, ...props }: DrawerPortalProps): ReactElement {
    return (
        <DialogPrimitive.Portal data-slot="drawer-portal" {...props}>
            {children}
        </DialogPrimitive.Portal>
    );
}

export type DrawerOverlayProps = BackdropPrimitiveProps;

export function DrawerOverlay({ className, ...props }: DrawerOverlayProps): ReactElement {
    return (
        <DialogPrimitive.Backdrop
            data-slot="drawer-overlay"
            // Base UI drops a nested surface's backdrop, undimming the page behind it.
            forceRender
            className={cn('motion-scrim fixed inset-0 z-50 bg-scrim/40', className)}
            {...props}
        />
    );
}

export type DrawerContentProps = PopupPrimitiveProps & { side?: DrawerSide };

export function DrawerContent({
    className,
    children,
    ref,
    side = 'right',
    ...props
}: DrawerContentProps): ReactElement {
    const panelRef = useRef<HTMLDivElement>(null);
    const popupRef = useMergedRefs(ref, panelRef);

    return (
        <DrawerPortal>
            <DrawerOverlay />
            <DialogPrimitive.Viewport
                data-side={side}
                className={cn(
                    'pointer-events-none fixed inset-0 z-50 flex',
                    side === 'right' ? 'justify-end' : 'justify-start',
                )}
            >
                <Container
                    level={1}
                    render={
                        <DialogPrimitive.Popup
                            data-slot="drawer-content"
                            data-side={side}
                            ref={popupRef}
                            // Focus the panel, not its first field: that would scroll
                            // the body and pop the mobile keyboard.
                            initialFocus={panelRef}
                            className={cn(
                                'pointer-events-auto relative z-10 flex h-full min-h-0 w-full flex-col',
                                'overflow-hidden bg-container text-content outline-none',
                                'motion-panel sm:w-4/5 sm:max-w-5xl sm:shadow-2xl',
                                // Docked edge only, once the panel stops being full-bleed.
                                'sm:border-line-weak',
                                side === 'right' ? 'sm:border-l' : 'sm:border-r',
                                className,
                            )}
                            {...props}
                        />
                    }
                >
                    {children}
                </Container>
            </DialogPrimitive.Viewport>
        </DrawerPortal>
    );
}

export type DrawerHeaderProps = ComponentPropsWithRef<'div'>;

export function DrawerHeader({ className, ...props }: DrawerHeaderProps): ReactElement {
    return (
        // Hairline and fold shadow come from containers/dialog-drawer.css.
        <Container
            // Nesting a level deeper would tighten the gutter out of step with the body.
            nestLevel={0}
            data-slot="drawer-header"
            className={cn(
                'relative z-10 flex shrink-0 items-center gap-container-cozy',
                'px-container py-container-cozy',
                'border-b',
                className,
            )}
            {...props}
        />
    );
}

export type DrawerBodyProps = ComponentPropsWithRef<'div'>;

export function DrawerBody({ className, ...props }: DrawerBodyProps): ReactElement {
    return (
        <div
            data-slot="drawer-body"
            className={cn(
                'flex min-h-0 flex-1 flex-col gap-container-cozy overflow-auto overscroll-contain',
                'px-container py-container',
                className,
            )}
            {...props}
        />
    );
}

export type DrawerFooterProps = ComponentPropsWithRef<'div'>;

export function DrawerFooter({ className, ...props }: DrawerFooterProps): ReactElement {
    return (
        <Container
            nestLevel={0}
            data-slot="drawer-footer"
            className={cn(
                'relative z-10 flex shrink-0 flex-row justify-end gap-container-cozy',
                'px-container py-container-cozy',
                'border-t',
                // The panel reaches the bottom edge, so clear the home indicator.
                'pb-[max(var(--spacing-container-cozy),env(safe-area-inset-bottom))]',
                className,
            )}
            {...props}
        />
    );
}

export type DrawerTitleProps = ComponentPropsWithRef<'h2'>;

export function DrawerTitle({ className, ...props }: DrawerTitleProps): ReactElement {
    return (
        <DialogPrimitive.Title
            data-slot="drawer-title"
            // A rung below `DialogTitle`: a drawer titles a page-like detail view, whose
            // long names would crowd the band and truncate sooner at heading-2.
            className={cn('min-w-0 flex-1 truncate text-heading-3', className)}
            {...props}
        />
    );
}

export type DrawerCloseProps = ComponentPropsWithRef<'button'> & ButtonVariantProps;

export function DrawerClose({ className, children, ...props }: DrawerCloseProps): ReactElement {
    return (
        <DialogPrimitive.Close
            data-slot="drawer-close"
            render={
                <Button className={className} {...props}>
                    {children}
                </Button>
            }
        />
    );
}

export type DrawerIconCloseProps = DrawerCloseProps & {
    /** Accessible name for the X. Pass the caller's translation of "Close". */
    label?: string;
};

export function DrawerIconClose({
    className,
    children,
    label = 'Close',
    ...props
}: DrawerIconCloseProps): ReactElement {
    return (
        <DialogPrimitive.Close
            data-slot="drawer-close"
            render={
                <IconButton label={label} className={className} {...props}>
                    {children ?? <XIcon className="size-5" />}
                </IconButton>
            }
        />
    );
}
